/**
 * A/B Testing Dashboard
 * Klorum - Phase 3
 *
 * Manage and monitor A/B experiments for AI responses.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const ABTesting = () => {
  const { t } = useTranslation();
  const [experiments, setExperiments] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [experimentDetails, setExperimentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [experimentsRes, statsRes] = await Promise.all([
        api.get('/ab-testing/experiments'),
        api.get('/ab-testing/stats/summary'),
      ]);

      setExperiments(experimentsRes.data.experiments || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching A/B testing data:', err);
      setError(t("abTesting.errors.loadData"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (experimentId) => {
    try {
      const response = await api.get(`/ab-testing/experiments/${experimentId}`);
      setExperimentDetails(response.data);
      setSelectedExperiment(experimentId);
      setDetailsOpen(true);
    } catch (err) {
      console.error('Error fetching experiment details:', err);
    }
  };

  const handleStopExperiment = async (experimentId) => {
    try {
      await api.post(`/ab-testing/experiments/${experimentId}/stop`);
      fetchData();
    } catch (err) {
      console.error('Error stopping experiment:', err);
    }
  };

  const getStatusColor = (isActive) => (isActive ? 'success' : 'default');

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t("abTesting.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("abTesting.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          {t("abTesting.actions.refresh")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Summary */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScienceIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("abTesting.stats.totalExperiments")}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {stats.total_experiments}
                </Typography>
                <Chip
                  size="small"
                  label={t("abTesting.stats.activeLabel", { count: stats.active_experiments })}
                  color="success"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon color="info" />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("abTesting.stats.totalImpressions")}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {formatNumber(stats.total_impressions)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("abTesting.stats.conversions")}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {formatNumber(stats.total_conversions)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrophyIcon color="warning" />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("abTesting.stats.conversionRate")}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {stats.overall_conversion_rate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Experiments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {t("abTesting.table.title")}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("abTesting.table.headers.experiment")}</TableCell>
                  <TableCell>{t("abTesting.table.headers.description")}</TableCell>
                  <TableCell align="center">{t("abTesting.table.headers.variants")}</TableCell>
                  <TableCell align="center">{t("abTesting.table.headers.impressions")}</TableCell>
                  <TableCell align="center">{t("abTesting.table.headers.status")}</TableCell>
                  <TableCell align="center">{t("abTesting.table.headers.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experiments.map((experiment) => (
                  <TableRow key={experiment.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {experiment.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("abTesting.table.idLabel", { id: experiment.id })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {experiment.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={experiment.variants_count}
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(experiment.total_impressions)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          experiment.is_active
                            ? t("abTesting.table.status.active")
                            : t("abTesting.table.status.stopped")
                        }
                        color={getStatusColor(experiment.is_active)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("abTesting.table.tooltips.viewDetails")}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(experiment.id)}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      {experiment.is_active && (
                        <Tooltip title={t("abTesting.table.tooltips.stopExperiment")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleStopExperiment(experiment.id)}
                          >
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Experiment Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {experimentDetails && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">{experimentDetails.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {experimentDetails.description}
                  </Typography>
                </Box>
                <Chip
                  label={
                    experimentDetails.is_active
                      ? t("abTesting.table.status.active")
                      : t("abTesting.table.status.stopped")
                  }
                  color={experimentDetails.is_active ? 'success' : 'default'}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* Statistical Significance */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("abTesting.dialog.progressTitle")}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={experimentDetails.statistical_significance?.progress_percentage || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t("abTesting.dialog.samples", {
                      count: experimentDetails.statistical_significance?.total_samples || 0,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("abTesting.dialog.goal", {
                      count: experimentDetails.statistical_significance?.target_samples || 0,
                    })}
                  </Typography>
                </Box>
                {experimentDetails.statistical_significance?.is_significant && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {t("abTesting.dialog.significant")}
                  </Alert>
                )}
              </Box>

              {/* Winner */}
              {experimentDetails.winner && (
                <Alert
                  severity="info"
                  icon={<TrophyIcon />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2">
                    {t("abTesting.dialog.winnerTitle", { name: experimentDetails.winner.name })}
                  </Typography>
                  <Typography variant="caption">
                    {t("abTesting.dialog.score", { score: experimentDetails.winner.score })}
                  </Typography>
                </Alert>
              )}

              {/* Variants Table */}
              <Typography variant="subtitle2" gutterBottom>
                {t("abTesting.dialog.variantsTitle")}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("abTesting.dialog.variantsHeaders.variant")}</TableCell>
                      <TableCell align="right">{t("abTesting.dialog.variantsHeaders.impressions")}</TableCell>
                      <TableCell align="right">{t("abTesting.dialog.variantsHeaders.conversions")}</TableCell>
                      <TableCell align="right">{t("abTesting.dialog.variantsHeaders.conversionRate")}</TableCell>
                      <TableCell align="right">{t("abTesting.dialog.variantsHeaders.satisfaction")}</TableCell>
                      <TableCell align="right">{t("abTesting.dialog.variantsHeaders.escalations")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {experimentDetails.variants?.map((variant) => (
                      <TableRow key={variant.variant_id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {variant.name}
                            </Typography>
                            {variant.is_control && (
                              <Chip size="small" label={t("abTesting.dialog.control")} variant="outlined" />
                            )}
                            {experimentDetails.winner?.variant_id === variant.variant_id && (
                              <TrophyIcon fontSize="small" color="warning" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(variant.metrics.impressions)}
                        </TableCell>
                        <TableCell align="right">
                          {variant.metrics.conversions}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={
                              variant.metrics.conversion_rate > 5
                                ? 'success.main'
                                : 'text.primary'
                            }
                          >
                            {variant.metrics.conversion_rate}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {variant.metrics.avg_satisfaction}/5
                        </TableCell>
                        <TableCell align="right">
                          {variant.metrics.escalation_rate}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>{t("abTesting.dialog.actions.close")}</Button>
              {experimentDetails.is_active && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => {
                    handleStopExperiment(selectedExperiment);
                    setDetailsOpen(false);
                  }}
                >
                  {t("abTesting.dialog.actions.stop")}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ABTesting;
